import { useState, useEffect } from 'react';
import { B } from '../constants/brand';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Btn, Input, Textarea, Select, Modal, ModalFooter, Card, Table, KpiCard, Badge, StatusBadge, Spinner, ErrorMsg, useToast, PageHeader, EmptyState, Icon } from '../components/ui';
import { FARM_TYPES, FARM_STATUS, num } from '../constants/farm';
import { FarmDetail } from './FarmDetail';

const EMPTY_FARM = { dealId: '', name: '', farmType: '', totalAcres: '', location: '', status: 'Active', description: '' };

export const Farms = ({ targetFarmId, onTargetHandled }) => {
  const { toast } = useToast();
  const [selected, setSelected] = useState(null);   // selected farm → detail view
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FARM);
  const [saving, setSaving] = useState(false);

  const { data: farms, loading, error, reload } = useApi(() => api.get('farms'), []);

  // Auto-open a farm when navigated from another page (e.g. global search).
  useEffect(() => {
    if (!targetFarmId || !farms) return;
    const row = farms.find(f => f.farmID === targetFarmId);
    if (row) { setSelected(row); onTargetHandled?.(); }
  }, [targetFarmId, farms]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data: dealOptions } = useApi(() => api.get('farm-deal-options'), []);

  const openCreate = () => { setForm(EMPTY_FARM); setShowModal(true); };

  const save = async () => {
    if (!form.dealId)       { toast.error('Select the deal this farm belongs to'); return; }
    if (!form.name.trim())  { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await api.post('farms', form);
      toast.success('Farm created');
      setShowModal(false);
      reload();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  if (selected) {
    return <FarmDetail farm={selected} onBack={() => { setSelected(null); reload(); }} />;
  }

  const list = farms || [];
  const totalAcres = list.reduce((s, f) => s + (Number(f.totalAcres) || 0), 0);
  const totalOpen  = list.reduce((s, f) => s + (Number(f.openTaskCount) || 0), 0);

  const dealLabel = (o) => `${o.dealName}${o.parcelCount ? ` (${o.parcelCount} parcel${o.parcelCount > 1 ? 's' : ''})` : ''}`;

  const columns = [
    { key: 'name', label: 'Farm', render: (v, r) => (
      <div>
        <div style={{ fontWeight: 700, color: B.navy }}>{v}</div>
        {r.location && <div style={{ fontSize: 11, color: B.textLt }}>{r.location}</div>}
      </div>
    )},
    { key: 'dealName', label: 'Deal', render: (v, r) => <span style={{ fontSize: 12 }}>{v}{r.parcelCount ? <span style={{ color: B.textLt }}> · {r.parcelCount} parcel{r.parcelCount > 1 ? 's' : ''}</span> : null}</span> },
    { key: 'farmType', label: 'Type', render: v => v ? <Badge color={B.tealDk}>{v}</Badge> : '—' },
    { key: 'totalAcres', label: 'Acres', render: v => num(v) },
    { key: 'fieldCount', label: 'Fields', render: v => v || 0 },
    { key: 'openTaskCount', label: 'Open Tasks', render: v => v ? <Badge color={B.amber}>{v}</Badge> : <span style={{ color: B.textLt }}>0</span> },
    { key: 'status', label: 'Status', render: v => <StatusBadge status={v} /> },
  ];

  return (
    <div>
      <PageHeader
        icon="farms"
        title="Farms"
        subtitle="Agricultural operations on investment-held land"
        actions={<Btn onClick={openCreate}><Icon name="add" size={15} /> New Farm</Btn>}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <KpiCard label="Farms"       value={list.length}            color={B.navy} />
        <KpiCard label="Total Acres" value={num(totalAcres, 0)}      color={B.green} />
        <KpiCard label="Open Tasks"  value={totalOpen}               color={B.amber} />
      </div>

      <Card style={{ padding: 0 }}>
        {loading ? <Spinner />
          : error ? <div style={{ padding: 24 }}><ErrorMsg msg={error} onRetry={reload} /></div>
          : list.length === 0 ? (
            <EmptyState
              icon="farms"
              title="No farms yet"
              message="Track agricultural operations on your investment-held land."
              action={<Btn onClick={openCreate}><Icon name="add" size={15} /> New Farm</Btn>}
            />
          )
          : <Table columns={columns} data={list} onRowClick={setSelected} />}
      </Card>

      {showModal && (
        <Modal title="New Farm" onClose={() => setShowModal(false)}>
          <Select
            label="Deal *"
            value={form.dealId}
            onChange={v => setForm({ ...form, dealId: v })}
            options={[{ value: '', label: '— Select the deal this farm belongs to —' },
              ...((dealOptions || []).map(o => ({ value: o.dealID, label: dealLabel(o) })))]}
          />
          <div style={{ fontSize: 11, color: B.textLt, marginTop: -8, marginBottom: 8 }}>The farm pulls in all parcels from this deal. Map each field onto one of them.</div>
          <Input label="Name" value={form.name} onChange={v => setForm({ ...form, name: v })} required />
          <div className="harvus-responsive-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Select label="Type" value={form.farmType} onChange={v => setForm({ ...form, farmType: v })}
              options={FARM_TYPES.map(t => ({ value: t, label: t || '— Select type —' }))} />
            <Input label="Total Acres" type="number" value={form.totalAcres} onChange={v => setForm({ ...form, totalAcres: v })} />
          </div>
          <Input label="Location" value={form.location} onChange={v => setForm({ ...form, location: v })} placeholder="County, State or description" />
          <Select label="Status" value={form.status} onChange={v => setForm({ ...form, status: v })}
            options={FARM_STATUS.map(s => ({ value: s, label: s }))} />
          <Textarea label="Description" value={form.description} onChange={v => setForm({ ...form, description: v })} />
          <ModalFooter>
            <Btn variant="ghost" onClick={() => setShowModal(false)}>Cancel</Btn>
            <Btn onClick={save} loading={saving}>Create Farm</Btn>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
};
